import { useLabSchedulingContext } from 'context/LabSchedulingContext/LabSchedulingProvider'
import LabSelect from './LabSelect'
import AddressSelect from './AddressSelect'
import TimeSlotMap from './TimeSlotMap'
import ConfrmLabAppointment from './ConfrmLabAppointment'
import LabConfirmation from './LabConfirmation'
import { Box, Text, useMediaQuery, Flex } from '@chakra-ui/react'
import AppLogo from 'components/AppLogo'
import ModifyLabAppointment from './ModifyLabAppointment'
import { useLabScheduling } from 'hooks/useLabScheduling/useLabscheduling'
import LoadingState from 'components/Spinner'
import Cancellation from './Cancellation'
import { useFlags } from 'launchdarkly-react-client-sdk'
import BackButton from 'components/Button/BackButton'
import { useEffect, useRef } from 'react'
import { LabAppointmentStatus } from 'types/appointments'

const LabSchedulingRouter = () => {
  const {
    currentScreen,
    previousScreen,
    isInitializing,
    setCurrentScreen,
    populateAppointmentData,
  } = useLabSchedulingContext()
  const { isLabAppointmentsLoading, labAppointments } = useLabScheduling()
  const { labScheduling } = useFlags()
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const hasRedirected = useRef(false)

  useEffect(() => {
    // Only run this effect once when labAppointments are loaded
    if (hasRedirected.current || !labAppointments || isLabAppointmentsLoading) {
      return
    }

    const isSchedulingFlow =
      currentScreen === 'labSelect' ||
      currentScreen === 'address' ||
      currentScreen === 'timeslotMap'

    const bookedLabAppointment =
      labAppointments &&
      labAppointments.find(
        (appointment) => appointment.status === LabAppointmentStatus.BOOKED
      )

    // User can have at most one lab appointment - redirect to modify if trying to schedule a new one
    // Don't redirect if we're already on a URL with labAppointmentId (coming from successful booking/modification)
    if (
      isSchedulingFlow &&
      bookedLabAppointment &&
      !window.location.search.includes('labAppointmentId=')
    ) {
      hasRedirected.current = true
      const { id, confirmationNumber } = bookedLabAppointment

      // Update URL parameters without page reload
      const newSearchParams = new URLSearchParams(window.location.search)
      newSearchParams.set('isRedirect', 'true')
      newSearchParams.set('labAppointmentId', id)
      newSearchParams.set('confirmationNumber', confirmationNumber)

      window.history.pushState(
        null,
        '',
        `${window.location.pathname}?${newSearchParams.toString()}`
      )

      populateAppointmentData(bookedLabAppointment)
      setCurrentScreen('modify')
    }
  }, [
    labAppointments,
    isLabAppointmentsLoading,
    currentScreen,
    setCurrentScreen,
    populateAppointmentData,
  ])

  const getCurrentComponent = () => {
    if (!labScheduling) {
      return <>Not enabled</>
    }

    switch (currentScreen) {
      case 'labSelect':
        return <LabSelect />
      case 'address':
        return <AddressSelect />
      case 'timeslotMap':
        return <TimeSlotMap isMobile={isMobile} />
      case 'confirm':
        return <ConfrmLabAppointment />
      case 'confirmation':
        return <LabConfirmation />
      case 'modify':
        return <ModifyLabAppointment />
      case 'cancellation':
        return <Cancellation />
      default:
        return <Text>Sorry, something went wrong!</Text>
    }
  }

  const showBackButton =
    currentScreen !== 'confirmation' &&
    currentScreen !== 'modify' &&
    currentScreen !== 'cancellation'

  const component = getCurrentComponent()

  const isLoading = isInitializing || isLabAppointmentsLoading

  if (isMobile) {
    return (
      <Box height="100vh" width="100vw" display="flex" flexDirection="column">
        <Box
          position="sticky"
          top="0"
          bg="white"
          width="100%"
          zIndex="1000"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Flex justify="center" align="center" width="100%" padding="1rem">
            {showBackButton && (
              <BackButton
                onClick={() => previousScreen()}
                style={{
                  position: 'absolute',
                  left: '1rem',
                  margin: '0',
                  marginLeft: '.5rem',
                }}
              />
            )}
            <AppLogo />
          </Flex>
        </Box>
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding="1rem"
        >
          {isLoading && <LoadingState />}
          {!isLoading && component}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box position="absolute" left="1.675rem" top="1.5rem">
        <AppLogo />
        {showBackButton && (
          <BackButton
            onClick={() => previousScreen()}
            style={{ marginTop: '2rem' }}
          />
        )}
      </Box>
      {isLoading && <LoadingState />}
      {!isLoading && component}
    </>
  )
}

export default LabSchedulingRouter
